// The "Session" button, development builds only: everything behind it is
// lazy-loaded, and a production build drops it entirely.
//
// The NODE_ENV test is written out INLINE wherever it guards a module. Webpack
// decides what to bundle while parsing, before the minifier removes dead code,
// and it only sees a branch as dead when the condition is the literal test
// (NODE_ENV is replaced at build time). Behind a `DEV` variable, every
// production build shipped the panel's chunk and its bridge client (found
// 2026-10-28); scripts/check-no-session-link.js fails a build that does.
import React, { Suspense, useEffect, useState } from 'react';
import { lazyWithReload } from '../../utils/lazyWithReload';
import { useChat } from '../../contexts/ChatContext';
import { useAdmin } from '../../hooks/useAdmin';

const DEV = process.env.NODE_ENV === 'development';

const SessionLinkHost =
  process.env.NODE_ENV === 'development'
    ? lazyWithReload(() => import('./SessionLinkHost'))
    : null;

// Save and strip a pairing link's token the moment the app loads. A
// `require` inside the branch, so a production build never bundles the client.
if (process.env.NODE_ENV === 'development')
  require('./sessionLinkClient').captureUrlPairing(window);

const OPEN_KEY = 'sessionLink.open';
const readOpen = () => {
  try {
    return window.sessionStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
};
const writeOpen = (open) => {
  try {
    window.sessionStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // blocked storage: the panel just starts closed after a reload
  }
};

function Launcher() {
  // Kept across reloads: the session's own code edits reload the page.
  const [open, setOpen] = useState(readOpen);
  useEffect(() => writeOpen(open), [open]);
  const chat = useChat();
  return (
    <>
      <button
        type="button"
        className="sl-launcher"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 10000,
          padding: '6px 12px',
          borderRadius: 16,
          border: '1px solid #3f4a3c',
          background: '#3f4a3c',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        {/* Hiding the panel leaves the session running and its history kept;
            only page actions stop reaching this tab until it is reopened. */}
        {open ? 'Minimize session' : 'Session'}
      </button>
      {open && (
        <Suspense fallback={null}>
          <SessionLinkHost chatLocation={chat?.conversationContext?.location} />
        </Suspense>
      )}
    </>
  );
}

// For admins only (the user, 2026-10-28). Hiding the button is the page's
// half; the bridge refuses a non-admin's sign-in token on its own.
function AdminLauncher() {
  const { isAdmin, loading } = useAdmin();
  if (loading || !isAdmin) return null;
  return <Launcher />;
}

export default function SessionLinkLauncher() {
  if (!DEV) return null;
  return <AdminLauncher />;
}
